import { Container, Group, Badge, Stack, Text, Title, Divider, Loader, Alert } from '@mantine/core';
import { Suspense, useEffect, useState, type JSX } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Loading } from '../../components/Loading';
import { SideMenu } from '../../components/SideMenu';
import { useMedplum } from '@medplum/react';
import { DocumentStatus, FormCompletionStats } from '../../types/documents';
import { DocumentService } from '../../services/DocumentService';
import { useMedplumProfile } from '../../components/documents/IntakeSummary';
import { IntakeSummary } from '../../components/documents/IntakeSummary';
import { IconCheck, IconCircleDashed } from '@tabler/icons-react';
import { useDocumentsContext } from '../../contexts/DocumentsContext';

// Main component content
function DocumentsPageContent(): JSX.Element {
  const medplum = useMedplum();
  const { profile: patient } = useMedplumProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshKey } = useDocumentsContext();
  
  const [documents, setDocuments] = useState<DocumentStatus[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Calculate completion stats
  const stats: FormCompletionStats = DocumentService.getCompletionStats(documents);
  
  // Group documents by completion status and category
  const incompleteDocuments = documents.filter(doc => doc.status !== 'completed');
  const completedDocuments = documents.filter(doc => doc.status === 'completed');
  
  // Test intake form removed
  
  // Load the assigned forms when component mounts or refreshKey changes
  useEffect(() => {
    async function loadForms() {
      setLoading(true);
      setError(null);
      console.log('Loading forms... (refreshKey:', refreshKey, ')');
      
      try {
        // Check for the specific form ID that isn't loading
        console.log("DEBUG: Checking for specific form ID 0195e160-83a5-7679-aaa3-af0216fa37a4");
        try {
          const specificForm = await medplum.readResource('QuestionnaireResponse', '0195e160-83a5-7679-aaa3-af0216fa37a4');
          console.log("DEBUG: Found the specific form:", specificForm);
        } catch (specificErr) {
          console.error("DEBUG: Error finding specific form:", specificErr);
          
          // Try searching for it by ID
          console.log("DEBUG: Trying search with _id parameter");
          const searchById = await medplum.search('QuestionnaireResponse', {
            '_id': '0195e160-83a5-7679-aaa3-af0216fa37a4'
          });
          console.log("DEBUG: Search by ID results:", searchById);
        }
        
        // DEBUG: Fetch QuestionnaireResponses directly to see what's available
        console.log("DEBUG: Fetching all QuestionnaireResponses directly");
        const directFetch = await medplum.search('QuestionnaireResponse', {
          'subject': `Patient/${patient.id}`
        });
        
        console.log(`Direct fetch found ${directFetch.entry?.length || 0} QuestionnaireResponses`);
        if (directFetch.entry?.length) {
          directFetch.entry.forEach((entry, index) => {
            const resource = entry.resource;
            if (resource) {
              console.log(`QuestionnaireResponse ${index + 1}: ID=${resource.id}`);
              console.log(`  Status: ${resource.status || 'unknown'}`);
              console.log(`  Questionnaire: ${resource.questionnaire || 'none'}`);
              console.log(`  Extensions: ${resource.extension?.length || 0}`);
              if (resource.extension?.length) {
                resource.extension.forEach(ext => {
                  console.log(`    URL: ${ext.url}, Value: ${JSON.stringify(ext.valueBoolean || ext.valueCode || ext.valueInteger || '(complex)')}`);
                });
              }
            }
          });
        } else {
          // If no QuestionnaireResponses found, manually create one to test with
          console.log('No QuestionnaireResponses found. Creating a test one...');
          try {
            // Create a test questionnaire first
            const testQuestionnaire = await medplum.createResource({
              resourceType: 'Questionnaire',
              status: 'active',
              title: 'Test Patient Intake Form',
              name: 'test-intake-form',
              item: [
                {
                  linkId: 'q1',
                  text: 'What is your name?',
                  type: 'string',
                  required: true
                },
                {
                  linkId: 'q2',
                  text: 'How are you feeling today?',
                  type: 'choice',
                  answerOption: [
                    { valueString: 'Great' },
                    { valueString: 'Good' },
                    { valueString: 'Okay' },
                    { valueString: 'Not well' }
                  ]
                }
              ]
            });
            
            console.log('Created test questionnaire:', (testQuestionnaire as any).id);
            
            // Now create a QuestionnaireResponse linked to it
            const testResponse = await medplum.createResource({
              resourceType: 'QuestionnaireResponse',
              status: 'in-progress',
              subject: { reference: `Patient/${patient.id}` },
              questionnaire: `Questionnaire/${(testQuestionnaire as any).id}`,
              extension: [
                {
                  url: 'https://progressnotes.app/fhir/StructureDefinition/form-assignment',
                  valueBoolean: true
                },
                {
                  url: 'https://progressnotes.app/fhir/StructureDefinition/portal-visible',
                  valueBoolean: true
                },
                {
                  url: 'https://progressnotes.app/fhir/StructureDefinition/form-category',
                  valueCode: 'questionnaire'
                }
              ]
            });
            
            console.log('Created test questionnaire response:', (testResponse as any).id);
          } catch (err) {
            console.error('Error creating test QuestionnaireResponse:', err);
          }
        }
        
        // Check for related Task resources
        console.log("DEBUG: Checking for related Task resources");
        const relatedTasks = await medplum.search('Task', {
          'focus': `Patient/${patient.id}`,
          'code': 'https://progressnotes.app/fhir/CodeSystem/task-type|intake-workflow'
        });
        
        console.log(`Found ${relatedTasks.entry?.length || 0} related intake workflow tasks`);
        if (relatedTasks.entry?.length) {
          relatedTasks.entry.forEach((entry, index) => {
            const task = entry.resource;
            if (task) {
              console.log(`Task ${index + 1}: ID=${task.id}, Status=${task.status}`);
              if (task.input?.length) {
                console.log('  Task inputs:');
                task.input.forEach(input => {
                  console.log(`    Type: ${input.type?.text || 'unknown'}`);
                  console.log(`    Value: ${input.valueReference?.reference || input.valueString || '(other value type)'}`);
                });
              }
            }
          });
        }
        
        // Get assigned forms using our service
        const assignedForms = await DocumentService.getAssignedForms(medplum, patient);
        console.log(`DocumentService found ${assignedForms.length} assigned forms`);
        
        // Now process each assigned form to ensure it has the correct properties
        const enhancedForms = assignedForms.map(form => {
          // Special logging for our target form
          if (form.id === '0195e160-83a5-7679-aaa3-af0216fa37a4') {
            console.log("DEBUG: Found our target form in the assigned forms list:", form);
            
            // Only update the name if it doesn't already have one
            if (!form.name || form.name === 'Untitled Form') {
              form.name = "Initial Intake Questionnaire";
            }
          }
          
          // Ensure each form has a complete set of properties
          return {
            ...form,
            // Force some properties to be present
            category: form.category || 'questionnaire',
            name: form.name || 'Untitled Form',
            href: form.href || `/documents/forms/intake?id=${form.id}`
          };
        });
        
        setDocuments(enhancedForms);
      } catch (err) {
        console.error('Error loading documents:', err);
        setError('Unable to load your documents. Please try again later.');
        // Show empty list on error
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    }
    
    loadForms();
  }, [medplum, patient, refreshKey]); // Add refreshKey to dependencies
  
  const getStatusColor = (status: DocumentStatus['status']) => {
    switch (status) {
      case 'completed':
        return 'teal';
      case 'pending':
        return 'blue';
      case 'not-started':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: DocumentStatus['status']) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'pending':
        return 'In Progress';
      case 'not-started':
        return 'To Do';
      default:
        return 'Unknown';
    }
  };

  // Check for the current document based on location
  const currentDoc = documents.find(doc => {
    // Extract the document type from the URL
    const pathParts = location.pathname.split('/');
    const formType = pathParts[pathParts.length - 1];
    const formId = new URLSearchParams(location.search).get('id');
    
    // Match by href or ID
    return doc.href.includes(formType) || (formId && doc.id === formId);
  });

  const createMenuItem = (doc: DocumentStatus) => ({
    name: (
      <Group justify="space-between" w="100%" pr="sm">
        <Group>
          {doc.status === 'completed' ? (
            <IconCheck size={16} color="var(--mantine-color-teal-6)" />
          ) : (
            <IconCircleDashed size={16} color="var(--mantine-color-gray-6)" />
          )}
          <Text>{doc.name}</Text>
        </Group>
        <Badge 
          color={getStatusColor(doc.status)}
          variant="light"
          size="sm"
        >
          {getStatusLabel(doc.status)}
        </Badge>
      </Group>
    ),
    href: doc.href
  });

  const sideMenu = {
    title: 'Documents & Forms',
    menu: [
      // Forms to complete section
      {
        name: (
          <Title order={6} c="dimmed" style={{ marginTop: '1rem' }}>
            To Complete
          </Title>
        ),
        href: '#',
      },
      ...incompleteDocuments.map(createMenuItem),
      // Completed forms section
      {
        name: (
          <>
            <Divider my="md" />
            <Title order={6} c="dimmed">
              Completed
            </Title>
          </>
        ),
        href: '#',
      },
      ...completedDocuments.map(createMenuItem),
    ],
  };

  if (loading) {
    return (
      <Container>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Stack gap="xl">
        {/* Show banner for incomplete required forms */}
        {currentDoc?.status !== 'completed' && currentDoc?.required && (
          <Badge 
            color="blue" 
            size="lg" 
            radius="sm" 
            p="md"
            variant="light"
            w="100%"
            style={{ textAlign: 'center' }}
          >
            Please complete this form before your first appointment
          </Badge>
        )}
        
        {/* Add Task summary at the top */}
        <IntakeSummary />
        
        {/* Error display */}
        {error && (
          <Alert color="red" mb="xl">
            {error}
          </Alert>
        )}
        
        <Group align="top">
          {/* Side menu */}
          <SideMenu {...sideMenu} />
          
          {/* Main content area */}
          <div style={{ width: 800, flex: 800 }}>
            <Suspense fallback={<Loading />}>
              <Outlet />
            </Suspense>
          </div>
        </Group>
      </Stack>
    </Container>
  );
}

export const DocumentsPage = DocumentsPageContent;